import apiService from './ApiService';

const normalizeListResponse = (response, key) => {
  const list = response?.[key] || response?.[key?.charAt(0).toUpperCase() + key?.slice(1)] || response?.data || response || [];
  const items = Array.isArray(list) ? list : [];
  const total = response?.total ?? response?.Total ?? items.length;
  return {
    items,
    total,
    resumen: response?.resumen || response?.Resumen || null,
    materias: response?.materias || response?.Materias || [],
    profesores: response?.profesores || response?.Profesores || [],
    topProfesores: response?.topProfesores || response?.TopProfesores || []
  };
};

const appendArrayParam = (params, key, values) => {
  if (!Array.isArray(values)) return;
  values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .forEach((value) => params.append(key, value));
};

class AulaDocenteService {
  async getAulasDocentes(options = {}) {
    const params = new URLSearchParams();
    if (options.idColegio) params.append('idColegio', options.idColegio);
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.search) params.append('search', options.search);
    appendArrayParam(params, 'materiaIds', options.materiaIds);
    appendArrayParam(params, 'profesorIds', options.profesorIds);
    const qs = params.toString();
    const response = await apiService.get(`/aulas-docentes${qs ? `?${qs}` : ''}`);
    const { items, total, resumen, materias, profesores, topProfesores } = normalizeListResponse(response, 'aulas');
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total, resumen, materias, profesores, topProfesores };
    }
    return items;
  }

  async getAulaDocenteById(id) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const response = await apiService.get(`/aulas-docentes/${encodeURIComponent(id)}`);
    const list = response?.aulas || response?.Aulas || [];
    return Array.isArray(list) && list.length > 0 ? list[0] : response?.aula || response?.Aula || response;
  }

  async getAulaDocenteAccessDetalle(id) {
    if (!id) throw new Error('No se encontró el aula docente.');
    return apiService.get(`/aulas-docentes/${encodeURIComponent(id)}/detalle-acceso`);
  }

  async getAulaDocenteStats(id, options = {}) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const params = new URLSearchParams();
    if (options.from) params.append('from', options.from);
    if (options.to) params.append('to', options.to);
    const qs = params.toString();
    return apiService.get(`/aulas-docentes/${encodeURIComponent(id)}/estadisticas${qs ? `?${qs}` : ''}`);
  }

  async registrarAulaDocenteTelemetry(id, payload = {}) {
    if (!id) throw new Error('No se encontró el aula docente.');
    return apiService.post(`/aulas-docentes/${encodeURIComponent(id)}/telemetria`, payload);
  }

  async getProfesoresParaAulaDocente(idColegio, options = {}) {
    if (!idColegio) throw new Error('No hay un colegio activo seleccionado.');

    const params = new URLSearchParams();
    params.append('idColegio', idColegio);
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.search) params.append('search', options.search);

    const response = await apiService.get(`/aulas-docentes/profesores?${params.toString()}`);
    const { items, total } = normalizeListResponse(response, 'profesores');
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total };
    }
    return items;
  }

  async createAulaDocente(payload) {
    const response = await apiService.post('/aulas-docentes', payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos crear el aula docente.');
    }
    return response;
  }

  async updateAulaDocente(id, payload) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const response = await apiService.put(`/aulas-docentes/${encodeURIComponent(id)}`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos actualizar el aula docente.');
    }
    return response;
  }

  async updateAulaDocenteActivo(id, activo) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const response = await apiService.put(`/aulas-docentes/${encodeURIComponent(id)}/activo`, { activo });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos actualizar el estado del aula docente.');
    }
    return response;
  }

  async updateAulaDocenteRecursosVisibilidad(id, payload = {}) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const response = await apiService.put(`/aulas-docentes/${encodeURIComponent(id)}/recursos-visibilidad`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos actualizar la visibilidad de recursos.');
    }
    return response;
  }

  async deleteAulaDocente(id) {
    if (!id) throw new Error('No se encontró el aula docente.');
    const response = await apiService.delete(`/aulas-docentes/${encodeURIComponent(id)}`);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos eliminar el aula docente.');
    }
    return response;
  }

  async getAulaAsistentesIA(aulaId) {
    if (!aulaId) throw new Error('No se encontró el aula docente.');
    return apiService.get(`/aulas-docentes/${encodeURIComponent(aulaId)}/asistentes-ia`);
  }

  async addAulaAsistenteIA(aulaId, assistantId) {
    if (!aulaId || !assistantId) throw new Error('Faltan datos para asignar el asistente.');
    return apiService.post(`/aulas-docentes/${encodeURIComponent(aulaId)}/asistentes-ia`, {
      idOpenAIAssistant: assistantId
    });
  }

  async removeAulaAsistenteIA(aulaId, assistantId) {
    if (!aulaId || !assistantId) throw new Error('Faltan datos para quitar el asistente.');
    return apiService.delete(`/aulas-docentes/${encodeURIComponent(aulaId)}/asistentes-ia/${encodeURIComponent(assistantId)}`);
  }

  async getMensajes(aulaId, options = {}) {
    return aulaDocenteMensajesService.getMensajes(aulaId, options);
  }

  async crearMensaje(aulaId, payload) {
    return aulaDocenteMensajesService.crearMensaje(aulaId, payload);
  }

  async actualizarMensaje(aulaId, mensajeId, payload) {
    return aulaDocenteMensajesService.actualizarMensaje(aulaId, mensajeId, payload);
  }

  async eliminarMensaje(aulaId, mensajeId) {
    return aulaDocenteMensajesService.eliminarMensaje(aulaId, mensajeId);
  }
}

const aulaDocenteMensajesService = {
  async getMensajes(aulaId, options = {}) {
    if (!aulaId) throw new Error('Aula docente requerida');
    const params = new URLSearchParams();
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.search) params.append('search', options.search);
    if (options.scope) params.append('scope', options.scope);
    const qs = params.toString();
    const response = await apiService.get(`/aulas-docentes/${encodeURIComponent(aulaId)}/mensajes${qs ? `?${qs}` : ''}`);
    const items = response?.items || response?.Items || [];
    const list = Array.isArray(items) ? items : [];
    const total = response?.total ?? response?.Total ?? list.length;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items: list, total };
    }
    return list;
  },

  async crearMensaje(aulaId, payload) {
    if (!aulaId) throw new Error('Aula docente requerida');
    const response = await apiService.post(`/aulas-docentes/${encodeURIComponent(aulaId)}/mensajes`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos crear el mensaje.');
    }
    return response;
  },

  async actualizarMensaje(aulaId, mensajeId, payload) {
    if (!aulaId || !mensajeId) throw new Error('Datos requeridos');
    const response = await apiService.put(`/aulas-docentes/${encodeURIComponent(aulaId)}/mensajes/${encodeURIComponent(mensajeId)}`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos actualizar el mensaje.');
    }
    return response;
  },

  async eliminarMensaje(aulaId, mensajeId) {
    if (!aulaId || !mensajeId) throw new Error('Datos requeridos');
    const response = await apiService.delete(`/aulas-docentes/${encodeURIComponent(aulaId)}/mensajes/${encodeURIComponent(mensajeId)}`);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      throw new Error(response.message || response.error || response.title || 'No pudimos eliminar el mensaje.');
    }
    return response;
  }
};

const aulaDocenteArchivosService = {
  async list(aulaId) {
    if (!aulaId) throw new Error('Aula docente requerida');
    return apiService.get(`/aulas-docentes/${encodeURIComponent(aulaId)}/archivos`);
  },

  async upload(aulaId, file) {
    if (!aulaId) throw new Error('Aula docente requerida');
    const formData = new FormData();
    formData.append('file', file);
    return apiService.request(`/aulas-docentes/${encodeURIComponent(aulaId)}/archivos`, {
      method: 'POST',
      body: formData
    });
  },

  async delete(aulaId, archivoId) {
    if (!aulaId || !archivoId) throw new Error('Datos requeridos');
    return apiService.delete(`/aulas-docentes/${encodeURIComponent(aulaId)}/archivos/${encodeURIComponent(archivoId)}`);
  },

  async downloadZip(aulaId) {
    const url = `${apiService.baseURL}/aulas-docentes/${encodeURIComponent(aulaId)}/archivos/zip`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('No pudimos descargar el ZIP.');
    return response.blob();
  },

  async downloadFile(aulaId, archivoId) {
    const url = `${apiService.baseURL}/aulas-docentes/${encodeURIComponent(aulaId)}/archivos/${encodeURIComponent(archivoId)}`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('No pudimos descargar el archivo.');
    return response.blob();
  }
};

const aulaDocenteLinksService = {
  async list(aulaId) {
    if (!aulaId) throw new Error('Aula docente requerida');
    return apiService.get(`/aulas-docentes/${encodeURIComponent(aulaId)}/links`);
  },

  async create(aulaId, payload) {
    if (!aulaId) throw new Error('Aula docente requerida');
    return apiService.post(`/aulas-docentes/${encodeURIComponent(aulaId)}/links`, {
      url: payload?.url || '',
      descripcion: payload?.descripcion || ''
    });
  },

  async update(aulaId, linkId, payload) {
    if (!aulaId || !linkId) throw new Error('Datos requeridos');
    return apiService.put(`/aulas-docentes/${encodeURIComponent(aulaId)}/links/${encodeURIComponent(linkId)}`, {
      url: payload?.url || '',
      descripcion: payload?.descripcion || '',
      activo: payload?.activo !== false
    });
  },

  async delete(aulaId, linkId) {
    if (!aulaId || !linkId) throw new Error('Datos requeridos');
    return apiService.delete(`/aulas-docentes/${encodeURIComponent(aulaId)}/links/${encodeURIComponent(linkId)}`);
  }
};

const aulaDocenteService = new AulaDocenteService();

export {
  aulaDocenteArchivosService,
  aulaDocenteLinksService,
  aulaDocenteMensajesService
};

export default aulaDocenteService;
